import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, CheckCircle, Circle } from "@phosphor-icons/react";
import { format, parseISO, isSameDay } from "date-fns";

const CATEGORY_PALETTES = {
  dark:    { Strength: "#E05A1D", Mass: "#8B5CF6", Cardio: "#22C55E", Flexibility: "#06B6D4", Custom: "#71717A" },
  light:   { Strength: "#E05A1D", Mass: "#8B5CF6", Cardio: "#22C55E", Flexibility: "#06B6D4", Custom: "#71717A" },
  crimson: { Strength: "#DC2626", Mass: "#BE123C", Cardio: "#F97316", Flexibility: "#EAB308", Custom: "#71717A" },
  cyan:    { Strength: "#06B6D4", Mass: "#3B82F6", Cardio: "#14B8A6", Flexibility: "#67E8F9", Custom: "#71717A" },
};

function useThemePalette() {
  const [theme, setTheme] = useState(() =>
    (typeof document !== "undefined" && document.documentElement.getAttribute("data-theme")) || "dark"
  );
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute("data-theme") || "dark");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return CATEGORY_PALETTES[theme] || CATEGORY_PALETTES.dark;
}

export default function ScheduleCalendar({ schedule, templates, api, onRefresh }) {
  const palette = useThemePalette();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [workoutName, setWorkoutName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [entryNotes, setEntryNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const scheduledDates = schedule.map((s) => {
    try { return parseISO(s.date); } catch { return null; }
  }).filter(Boolean);

  // Build per-category date buckets so each scheduled day shows its category color
  const CATEGORY_KEYS = ["strength", "mass", "cardio", "flexibility", "custom"];
  const buckets = { strength: [], mass: [], cardio: [], flexibility: [], custom: [] };
  const seen = new Set();
  schedule.forEach((s) => {
    if (!s.date || seen.has(s.date)) return;
    seen.add(s.date);
    let category = "custom";
    if (s.template_id) {
      const t = templates.find((tpl) => tpl.id === s.template_id);
      if (t?.category) category = t.category.toLowerCase();
    }
    if (!CATEGORY_KEYS.includes(category)) category = "custom";
    try {
      buckets[category].push(parseISO(s.date));
    } catch { /* skip invalid */ }
  });

  const dayEntries = schedule.filter((s) => {
    try { return isSameDay(parseISO(s.date), selectedDate); } catch { return false; }
  });

  const handleAddEntry = async () => {
    if (!workoutName.trim()) { toast.error("Workout name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${api}/schedule`, {
        date: format(selectedDate, "yyyy-MM-dd"),
        workout_name: workoutName,
        template_id: templateId || null,
        notes: entryNotes,
        completed: false,
      });
      toast.success("Scheduled!");
      setShowAdd(false);
      setWorkoutName(""); setTemplateId(""); setEntryNotes("");
      onRefresh();
    } catch (e) {
      toast.error("Failed to schedule");
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (entry) => {
    try {
      await axios.put(`${api}/schedule/${entry.id}`, {
        ...entry,
        completed: !entry.completed,
      });
      onRefresh();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${api}/schedule/${id}`);
      toast.success("Removed from schedule");
      onRefresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const loadTemplate = (tplId) => {
    setTemplateId(tplId);
    const t = templates.find((tpl) => tpl.id === tplId);
    if (t) setWorkoutName(t.name);
  };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div>
        <h2
          className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          Training Schedule
        </h2>
        <p className="text-sm text-[#71717A] mt-1">Plan and track your training days</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
        {/* Calendar */}
        <div className="bg-[#161616] border border-[#27272A] p-4 space-y-3" data-testid="calendar-widget">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            modifiers={{
              scheduled: scheduledDates,
              catStrength: buckets.strength,
              catMass: buckets.mass,
              catCardio: buckets.cardio,
              catFlexibility: buckets.flexibility,
              catCustom: buckets.custom,
            }}
            modifiersClassNames={{
              scheduled: "workout-day",
              catStrength: "cat-strength",
              catMass: "cat-mass",
              catCardio: "cat-cardio",
              catFlexibility: "cat-flex",
              catCustom: "cat-custom",
            }}
            className="text-white"
          />
          {/* Legend */}
          <div className="border-t border-[#27272A] pt-3 grid grid-cols-2 gap-x-3 gap-y-1.5" data-testid="schedule-legend">
            {Object.entries(palette).map(([label, color]) => (
              <div key={label} className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA]">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Day Panel */}
        <div className="bg-[#161616] border border-[#27272A] p-5" data-testid="day-panel">
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-lg font-semibold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {format(selectedDate, "EEEE, MMM d")}
            </h3>
            <button
              onClick={() => setShowAdd(true)}
              className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs flex items-center gap-1.5 transition-colors"
              data-testid="schedule-add-btn"
            >
              <Plus size={14} weight="bold" /> Add
            </button>
          </div>

          {dayEntries.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[#27272A]">
              <p className="text-[#71717A] text-sm">No workouts scheduled for this day</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dayEntries.map((entry) => (
                <div
                  key={entry.id}
                  className={`border p-4 flex items-center justify-between transition-colors ${
                    entry.completed
                      ? "border-[#22C55E]/30 bg-[#22C55E]/5"
                      : "border-[#27272A] bg-[#0F0F0F]"
                  }`}
                  data-testid={`schedule-entry-${entry.id}`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleComplete(entry)}
                      className="transition-colors"
                      data-testid={`toggle-complete-${entry.id}`}
                    >
                      {entry.completed ? (
                        <CheckCircle size={22} weight="fill" className="text-[#22C55E]" />
                      ) : (
                        <Circle size={22} className="text-[#71717A] hover:text-[#E05A1D]" />
                      )}
                    </button>
                    <div>
                      <p className={`text-sm font-semibold ${entry.completed ? "text-[#22C55E] line-through" : "text-white"}`}>
                        {entry.workout_name}
                      </p>
                      {entry.notes && <p className="text-xs text-[#71717A] mt-0.5">{entry.notes}</p>}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteEntry(entry.id)}
                    className="text-[#71717A] hover:text-red-500 transition-colors"
                    data-testid={`delete-schedule-${entry.id}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Schedule Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#161616] border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-xl font-bold uppercase tracking-tight text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Schedule for {format(selectedDate, "MMM d")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {templates.length > 0 && (() => {
              const sorted = [...templates].sort((a, b) => {
                const aPreset = a.is_preset ? 1 : 0;
                const bPreset = b.is_preset ? 1 : 0;
                if (aPreset !== bPreset) return aPreset - bPreset;
                return (a.name || "").localeCompare(b.name || "");
              });
              const hasCustom = sorted.some((t) => !t.is_preset);
              const hasPresets = sorted.some((t) => t.is_preset);
              return (
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">From Template</label>
                  <Select onValueChange={loadTemplate}>
                    <SelectTrigger className="bg-[#0F0F0F] border-[#27272A] text-white" data-testid="schedule-template-select">
                      <SelectValue placeholder="Optional: pick a template" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-[#27272A]">
                      {hasCustom && (
                        <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#E05A1D]">Your Workouts</div>
                      )}
                      {sorted.filter((t) => !t.is_preset).map((t) => (
                        <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#262626]" data-testid={`schedule-template-option-${t.id}`}>
                          {t.name}
                        </SelectItem>
                      ))}
                      {hasCustom && hasPresets && (
                        <div className="px-2 py-1 mt-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#71717A] border-t border-[#27272A]">Workouts</div>
                      )}
                      {sorted.filter((t) => t.is_preset).map((t) => (
                        <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#262626]" data-testid={`schedule-template-option-${t.id}`}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Workout Name</label>
              <input
                type="text"
                value={workoutName}
                onChange={(e) => setWorkoutName(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                placeholder="e.g. Push Day"
                data-testid="schedule-workout-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Notes</label>
              <textarea
                value={entryNotes}
                onChange={(e) => setEntryNotes(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm h-16 resize-none focus:border-[#E05A1D] outline-none"
                placeholder="Optional notes"
                data-testid="schedule-notes-input"
              />
            </div>
            <button
              onClick={handleAddEntry}
              disabled={saving}
              className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-50"
              data-testid="schedule-save-btn"
            >
              {saving ? "Saving..." : "Schedule"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
